// Back and refresh like other apps (approved by Robert 2026-10-09: "needs to behave like the big boy apps").
// Pure: the page stack in, the move out. Field Access asks this on every page change, then tells the browser.
//
// Rules:
// * A page opened from its parent is a new step: the phone's Back gesture and the browser Back button return to it.
// * Going to the parent page, by a button or by the gesture, is one step back. Nothing is added.
// * Any other move replaces the current step, so Back never walks into a page that no longer applies
//   (signed out, finished, or another QEW).
// * The hash names the page so a refresh opens the same page while the visit is open.

/** The QEW-side pages. "guest" is the guest page itself, outside Field Access. */
export type Page =
  | "guest"
  | "gate"
  | "qew"
  | "discard"
  | "sopRead"
  | "jsp"
  | "jspOverlay"
  | "dashboard"
  | "loto"
  | "gloves"
  | "gloveArrive"
  | "gloveRemove"
  | "resources"
  | "sop";

/** The page Back returns to. fromAsset: the visit started at an asset label. */
export function parentOf(page: Page, fromAsset: boolean): Page | null {
  switch (page) {
    case "gate":
    case "qew":
      return fromAsset ? "guest" : null;
    case "discard":
    case "sopRead":
    case "jsp":
      return "qew";
    case "jspOverlay":
      return "jsp";
    case "dashboard":
      return fromAsset ? "qew" : null;
    case "loto":
    case "gloves":
    case "resources":
      return "dashboard";
    case "gloveArrive":
    case "gloveRemove":
      return "gloves";
    case "sop":
      return "resources";
    default:
      return null;
  }
}

/** The page and every page above it, top first: what Back walks through. */
export function chainOf(page: Page, fromAsset: boolean): Page[] {
  const out: Page[] = [page];
  let p = parentOf(page, fromAsset);
  while (p) {
    out.unshift(p);
    p = parentOf(p, fromAsset);
  }
  return out;
}

/** The hash a refresh reads. A page inside the JSP or a form reopens at the page above it. */
export function hashOf(page: Page): string {
  switch (page) {
    case "qew":
    case "discard":
    case "sopRead":
    case "jsp":
    case "jspOverlay":
      return "qew";
    case "dashboard":
    case "loto":
      return "dashboard";
    case "gloves":
    case "gloveArrive":
    case "gloveRemove":
      return "gloves";
    case "resources":
    case "sop":
      return "resources";
    default:
      return "";
  }
}

/** The page a refresh opens from the hash, or null for the normal first screen. */
export function pageFromHash(hash: string): Page | null {
  const h = hash.replace(/^#/, "");
  return h === "qew" || h === "dashboard" || h === "gloves" || h === "resources" ? h : null;
}

export type Move = "same" | "push" | "up" | "replace";

/** What a page change does to the stack. The stack's last entry is the page on screen. */
export function moveTo(stack: readonly Page[], page: Page, fromAsset: boolean): { move: Move; stack: Page[] } {
  const top = stack[stack.length - 1];
  if (page === top) return { move: "same", stack: [...stack] };
  if (stack.length > 1 && page === stack[stack.length - 2]) return { move: "up", stack: stack.slice(0, -1) };
  if (top !== undefined && parentOf(page, fromAsset) === top) return { move: "push", stack: [...stack, page] };
  return { move: "replace", stack: stack.length ? [...stack.slice(0, -1), page] : [page] };
}
